"use client";

import React from "react";
import StickyBox from "react-sticky-box";

import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import type { Breakpoint } from "@mui/material/styles";

import { Scrollbar } from "@onair/minimal-ui/scrollbar";
import type { NavSectionProps } from "@onair/minimal-ui/nav-section";
import {
	NavSectionMini,
	NavSectionVertical,
} from "@onair/minimal-ui/nav-section";

import { hideScrollY } from "@onair/config-mui/styles";
import { useSettingsStore, actionToggleSidebar } from "@onair/store/settings";

import { NavToggleButton } from "./nav-toggle-button";

// ----------------------------------------------------------------------

export type NavVerticalProps = NavSectionProps & {
	layoutQuery: Breakpoint;
	slots?: {
		topArea?: React.ReactNode;
		bottomArea?: React.ReactNode;
	};
};

export const NavVertical = ({
	sx,
	data,
	slots,
	layoutQuery,
	...other
}: NavVerticalProps) => {
	const theme = useTheme();
	const settings = useSettingsStore();

	const renderNavVertical = (
		<Scrollbar fillContent>
			<NavSectionVertical
				data={data}
				sx={{ px: 2, flex: "1 1 auto" }}
				{...other}
			/>

			{slots?.bottomArea}
		</Scrollbar>
	);

	const renderNavMini = (
		<>
			<NavSectionMini
				data={data}
				sx={{
					pb: 2,
					px: 0.5,
					...hideScrollY,
					flex: "1 1 auto",
					overflowY: "auto",
				}}
				{...other}
			/>
			{slots?.bottomArea}
		</>
	);

	return (
		<Box
			sx={{
				// display: "none",
				position: "relative",
				flexDirection: "column",
				bgcolor: "var(--layout-nav-bg)",
				// zIndex: 'var(--layout-nav-zIndex)',
				width: settings.sidebarCollapse
					? "var(--layout-nav-mini-width)"
					: "var(--layout-nav-vertical-width)",
				borderRight: `1px solid var(--layout-nav-border-color)`,
				transition: theme.transitions.create(["width"], {
					easing: "var(--layout-transition-easing)",
					duration: "var(--layout-transition-duration)",
				}),
				[theme.breakpoints.up(layoutQuery)]: {
					display: "flex",
				},
				zIndex: 10,
				pb: 3,
				...sx,
			}}
		>
			<StickyBox className="stickyBox" offsetTop={72} offsetBottom={20}>
				<NavToggleButton
					isNavMini={settings.sidebarCollapse}
					onClick={() => actionToggleSidebar(!settings.sidebarCollapse)}
					sx={{
						display: "none",
						left: "100%",
						[theme.breakpoints.up(layoutQuery)]: {
							display: "inline-flex",
						},
					}}
				/>
				<div className="py-2">
					{settings.sidebarCollapse ? renderNavMini : renderNavVertical}
				</div>
			</StickyBox>
		</Box>
	);
};
